import React, { useRef, useState, useCallback } from 'react';
import { Upload, RotateCcw, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { putBrandingFile } from '../../store/actions/adminActions';

export const BRANDING_ASSET_ALLOWED = {
  logo: ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'],
  favicon: [
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/svg+xml',
    'image/x-icon',
    'image/vnd.microsoft.icon',
  ],
  logoEmail: ['image/png', 'image/jpeg', 'image/webp', 'image/x-icon', 'image/vnd.microsoft.icon'],
};

const extToMime = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  ico: 'image/x-icon',
};

/**
 * Infer Content-Type for presign; favicon .ico maps to image/x-icon.
 */
export const inferBrandingContentType = (file, assetType) => {
  if (file.type && file.type !== 'application/octet-stream') return file.type;
  const name = (file.name || '').toLowerCase();
  const ext = name.split('.').pop();
  if (ext === 'ico') return 'image/x-icon';
  return extToMime[ext] || '';
};

export const isAllowedBrandingMime = (assetType, contentType) =>
  BRANDING_ASSET_ALLOWED[assetType]?.includes(contentType);

/**
 * Single branding asset: upload to preview (S3) or paste external URL.
 */
const WhiteLabelBrandingAssetUpload = ({
  assetType,
  label,
  description,
  whiteLabelId,
  savedUrl,
  pendingValue,
  onPendingChange,
  presignWhiteLabelBranding,
  disabled,
}) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [localError, setLocalError] = useState('');
  const [externalDraft, setExternalDraft] = useState('');

  const displayUrl =
    pendingValue !== undefined ? pendingValue || '' : savedUrl || '';
  const hasPendingChange = pendingValue !== undefined;
  const accept = BRANDING_ASSET_ALLOWED[assetType]
    .map((m) => {
      if (m === 'image/svg+xml') return '.svg';
      if (m === 'image/png') return '.png';
      if (m === 'image/jpeg') return '.jpg,.jpeg';
      if (m === 'image/webp') return '.webp';
      if (m === 'image/x-icon' || m === 'image/vnd.microsoft.icon') return '.ico';
      return '';
    })
    .filter(Boolean)
    .join(',');

  const handleFile = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file || !whiteLabelId) return;
      setLocalError('');
      const contentType = inferBrandingContentType(file, assetType);
      if (!contentType || !isAllowedBrandingMime(assetType, contentType)) {
        setLocalError(
          assetType === 'logoEmail'
            ? 'Use PNG, JPEG, WebP, or ICO. SVG is not supported for email logos.'
            : 'File type not allowed for this asset.',
        );
        return;
      }
      setUploading(true);
      try {
        const data = await presignWhiteLabelBranding({
          whiteLabelId,
          assetType,
          contentType,
        });
        const uploadUrl = data?.uploadUrl ?? data?.data?.uploadUrl;
        const publicUrl = data?.publicUrl ?? data?.data?.publicUrl;
        if (!uploadUrl || !publicUrl) {
          throw new Error('Invalid presign response');
        }
        await putBrandingFile(uploadUrl, file, contentType);
        onPendingChange(publicUrl);
      } catch (err) {
        setLocalError(err?.message || 'Upload failed');
      } finally {
        setUploading(false);
      }
    },
    [assetType, whiteLabelId, presignWhiteLabelBranding, onPendingChange],
  );

  const handleRevert = () => {
    setLocalError('');
    setExternalDraft('');
    onPendingChange(undefined);
  };

  const applyExternalUrl = () => {
    setLocalError('');
    const u = (externalDraft || '').trim();
    if (!u) {
      onPendingChange('');
      return;
    }
    if (!/^https:\/\//i.test(u)) {
      setLocalError('URL must start with https://');
      return;
    }
    onPendingChange(u);
  };

  return (
    <div className='rounded-lg border border-border bg-card p-4 space-y-3'>
      <div>
        <Label className='text-sm font-semibold text-foreground'>{label}</Label>
        {description && <p className='text-xs text-muted-foreground mt-1'>{description}</p>}
      </div>
      <div className='flex flex-wrap items-start gap-4'>
        <div className='flex h-16 w-16 shrink-0 items-center justify-center rounded-md border border-border bg-muted/30 overflow-hidden'>
          {displayUrl ? (
            <img src={displayUrl} alt='' className='max-h-full max-w-full object-contain' />
          ) : (
            <span className='text-[10px] text-muted-foreground text-center px-1'>No image</span>
          )}
        </div>
        <div className='flex flex-col gap-2 min-w-[200px] flex-1'>
          <input
            ref={inputRef}
            type='file'
            accept={accept}
            className='hidden'
            onChange={handleFile}
            disabled={disabled || uploading || !whiteLabelId}
          />
          <div className='flex flex-wrap gap-2'>
            <Button
              type='button'
              variant='outline'
              size='sm'
              disabled={disabled || uploading || !whiteLabelId}
              onClick={() => inputRef.current?.click()}
              className='gap-2'>
              {uploading ? <Loader2 size={14} strokeWidth={1.5} className='animate-spin' /> : <Upload size={14} strokeWidth={1.5} />}
              Upload
            </Button>
            {hasPendingChange && (
              <Button type='button' variant='ghost' size='sm' onClick={handleRevert} className='gap-2'>
                <RotateCcw size={14} strokeWidth={1.5} />
                Revert
              </Button>
            )}
          </div>
          <div className='flex flex-col sm:flex-row gap-2 items-stretch sm:items-end'>
            <div className='flex-1 min-w-0'>
              <Label className='text-xs text-muted-foreground'>Or paste external HTTPS URL</Label>
              <Input
                className='mt-1 bg-input border-border'
                value={externalDraft}
                onChange={(e) => setExternalDraft(e.target.value)}
                placeholder='https://…'
                disabled={disabled}
              />
            </div>
            <Button type='button' variant='secondary' size='sm' onClick={applyExternalUrl} disabled={disabled}>
              Use URL
            </Button>
          </div>
          {localError && <p className='text-xs text-destructive'>{localError}</p>}
        </div>
      </div>
    </div>
  );
};

export default WhiteLabelBrandingAssetUpload;
